import React, { createContext, useContext, useEffect, useState } from "react";
import { api } from "../lib/api";

const AuthContext = createContext(null);
const TOKEN_KEY = "elegant_affairs_admin_token";

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || null);
  const [checking, setChecking] = useState(true);
  const [authed, setAuthed] = useState(false);

  // Attach/remove axios Authorization header whenever the token changes
  useEffect(() => {
    if (token) {
      api.defaults.headers.common.Authorization = `Bearer ${token}`;
      localStorage.setItem(TOKEN_KEY, token);
    } else {
      delete api.defaults.headers.common.Authorization;
      localStorage.removeItem(TOKEN_KEY);
    }
  }, [token]);

  // Validate token on mount or whenever it changes
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!token) {
        if (!cancelled) {
          setAuthed(false);
          setChecking(false);
        }
        return;
      }
      try {
        await api.get("/admin/me");
        if (!cancelled) setAuthed(true);
      } catch (_) {
        if (!cancelled) {
          setAuthed(false);
          setToken(null);
        }
      } finally {
        if (!cancelled) setChecking(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  // Global 401 interceptor — log out on expired/invalid tokens
  useEffect(() => {
    const id = api.interceptors.response.use(
      (r) => r,
      (err) => {
        if (err?.response?.status === 401 && token) {
          setToken(null);
          setAuthed(false);
        }
        return Promise.reject(err);
      }
    );
    return () => api.interceptors.response.eject(id);
  }, [token]);

  const login = async (password) => {
    const r = await api.post("/admin/login", { password });
    setToken(r.data.token);
    setAuthed(true);
    return r.data.token;
  };

  const logout = () => {
    setToken(null);
    setAuthed(false);
  };

  return (
    <AuthContext.Provider value={{ token, authed, checking, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
